/**
 * `<gs-cc-widget>` — Content projector for internal community widgets.
 *
 * Projects server-rendered content from a `<template shadowrootmode>` into
 * shadow DOM, injects component CSS, and hydrates Preact components.
 * Used standalone (when content is SSR'd) or inside `<gs-cc-embed-widget>`
 * (which fetches the content from the backend).
 *
 * Design tokens are not injected here — they inherit through the shadow boundary
 * from the document, which is what keeps widgets in sync with theme edits.
 *
 * Tracks stylesheet loading and signals readiness via `widget-css-ready`,
 * enabling the embed wrapper to keep its placeholder visible until CSS is loaded.
 */

import { injectWidgetStyles } from './design-tokens'

const CSS_GATE_TIMEOUT_MS = 5000

export class WidgetElement extends HTMLElement {
  private pendingReadyHandler: (() => void) | null = null
  private cssGateTimer: ReturnType<typeof setTimeout> | null = null
  private _cssReady = false

  /** Whether all shadow root stylesheets have finished loading. */
  get cssReady(): boolean {
    return this._cssReady
  }

  connectedCallback(): void {
    this._cssReady = false

    if (this.shadowRoot) {
      // Declarative shadow DOM: content is already visible and styled by the browser.
      injectWidgetStyles(this.shadowRoot)
      this.hydratePreact(this.shadowRoot)
      this.markCssReady()
      return
    }

    const template = this.querySelector<HTMLTemplateElement>('template[shadowrootmode]')

    if (!template) {
      console.warn('[WidgetSDK] Widget: No shadow root or template found.')
      return
    }

    const mode = (template.getAttribute('shadowrootmode') ?? 'open') as ShadowRootMode
    const shadowRoot = this.attachShadow({ mode })
    shadowRoot.appendChild(template.content.cloneNode(true))
    template.remove()

    injectWidgetStyles(shadowRoot)
    this.gateOnStylesheets(shadowRoot)
    this.hydratePreact(shadowRoot)
  }

  disconnectedCallback(): void {
    if (this.pendingReadyHandler) {
      document.removeEventListener('insidedReady', this.pendingReadyHandler)
      this.pendingReadyHandler = null
    }
    if (this.cssGateTimer) {
      clearTimeout(this.cssGateTimer)
      this.cssGateTimer = null
    }
    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = ''
    }
  }

  private markCssReady(): void {
    if (this._cssReady) return
    this._cssReady = true
    if (this.cssGateTimer) {
      clearTimeout(this.cssGateTimer)
      this.cssGateTimer = null
    }
    this.dispatchEvent(new CustomEvent('widget-css-ready', { bubbles: true, composed: true }))
  }

  /**
   * Tracks stylesheet loading and signals readiness via `widget-css-ready`.
   * The embed wrapper keeps its placeholder visible until this signal fires,
   * so no visual hiding is needed here — the slot already hides the widget.
   *
   * Only applies when shadow DOM is manually created (embed path);
   * declarative shadow DOM (SSR) skips this since CSS is already available.
   */
  private gateOnStylesheets(root: ShadowRoot): void {
    const links = Array.from(root.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
    const pending = links.filter((link) => !link.sheet)

    if (pending.length === 0) {
      this.markCssReady()
      return
    }

    let loaded = 0
    const onLoad = (): void => {
      loaded++
      if (loaded >= pending.length) this.markCssReady()
    }

    for (const link of pending) {
      link.addEventListener('load', onLoad, { once: true })
      link.addEventListener('error', onLoad, { once: true })
    }

    this.cssGateTimer = setTimeout(() => this.markCssReady(), CSS_GATE_TIMEOUT_MS)
  }

  private hydratePreact(root: ShadowRoot): void {
    const insided = (globalThis as any).insided
    if (typeof insided?.bindPreactComponents === 'function') {
      insided.bindPreactComponents(root)
      return
    }

    // Resources not loaded yet — wait for the Preact runtime to signal readiness.
    // bootstrap.ts dispatches 'insidedReady' on document after initializeApp()
    // sets window.insided.bindPreactComponents.
    this.pendingReadyHandler = (): void => {
      this.pendingReadyHandler = null
      const ready = (globalThis as any).insided
      if (typeof ready?.bindPreactComponents === 'function') {
        ready.bindPreactComponents(root)
      }
    }
    document.addEventListener('insidedReady', this.pendingReadyHandler, { once: true })
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('gs-cc-widget')) {
  customElements.define('gs-cc-widget', WidgetElement)
}
